import { useCallback, useEffect, useState } from 'react'
import type { PaymentLink } from '@unified-lk/api-contracts'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { StaffShell } from '../StaffShell'
import {
  createAdhocPaymentLink,
  getPaymentOverview,
  type PaymentOverviewStats,
} from '../../api/payments'
import type { AuthSession } from '../../types/plans'

interface Props {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  navigation: Array<{ key: string; label: string }>
  onNavigate: (key: string) => void
  onLogout: () => void
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  pending: 'Ожидает',
  waiting_for_capture: 'Удержание',
  paid: 'Оплачено',
  canceled: 'Отменено',
  refunded: 'Возврат',
  expired: 'Истекло',
}

const STATUS_COLORS: Record<string, string> = {
  pending: '#f59e0b',
  waiting_for_capture: '#3b82f6',
  paid: '#22c55e',
  canceled: '#ef4444',
  refunded: '#8b5cf6',
  expired: '#9ca3af',
}

const SOURCE_LABELS: Record<string, string> = {
  mis_plan: 'МИС план',
  constructor_plan: 'Конструктор',
  adhoc: 'Разовая ссылка',
}

const SOURCE_COLORS: Record<string, string> = {
  mis_plan: '#6366f1',
  constructor_plan: '#0ea5e9',
  adhoc: '#f97316',
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatAmount(kopecks: number): string {
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(
    kopecks / 100,
  )
}

function formatDate(dt: string | null | undefined): string {
  if (!dt) return '—'
  try {
    return new Date(dt).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dt
  }
}

// ─── Stats row ─────────────────────────────────────────────────────────────────

function StatsRow({ stats }: { stats: PaymentOverviewStats | null }) {
  const cards = [
    { label: 'Всего платежей', value: stats ? String(stats.total) : '…', color: '#6b7280' },
    { label: 'Оплачено', value: stats ? String(stats.paid) : '…', color: '#22c55e' },
    { label: 'Ожидает', value: stats ? String(stats.pending) : '…', color: '#f59e0b' },
    { label: 'Отменено', value: stats ? String(stats.canceled) : '…', color: '#ef4444' },
    {
      label: 'Сумма оплачено',
      value: stats ? formatAmount(stats.total_paid_kopecks) : '…',
      color: '#2563eb',
    },
  ]

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
      {cards.map((card) => (
        <div
          key={card.label}
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            padding: '14px 18px',
          }}
        >
          <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500, marginBottom: 4 }}>
            {card.label}
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: card.color }}>{card.value}</div>
        </div>
      ))}
    </div>
  )
}

// ─── Unified links table ────────────────────────────────────────────────────────

function UnifiedTable({
  links,
  onRefresh,
  loading,
}: {
  links: PaymentLink[]
  onRefresh: () => void
  loading: boolean
}) {
  const [copiedId, setCopiedId] = useState<number | null>(null)

  function handleCopy(link: PaymentLink) {
    if (!link.payment_url) return
    void navigator.clipboard.writeText(link.payment_url).then(() => {
      setCopiedId(link.id)
      setTimeout(() => setCopiedId(null), 2000)
    })
  }

  if (loading) {
    return <p style={{ fontSize: 13, color: '#9ca3af', padding: '16px 0' }}>Загрузка...</p>
  }

  if (links.length === 0) {
    return (
      <p style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: '24px 0' }}>
        Платёжных ссылок нет
      </p>
    )
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
            {[
              '#',
              'Источник',
              'Статус',
              'Провайдер',
              'Сумма',
              'Описание / Пациент',
              'Создан',
              'Оплачен',
              '',
            ].map((h) => (
              <th
                key={h}
                style={{
                  padding: '9px 12px',
                  textAlign: 'left',
                  color: '#6b7280',
                  fontWeight: 600,
                  fontSize: 11,
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {links.map((link) => {
            const statusColor = STATUS_COLORS[link.status] ?? '#9ca3af'
            const statusLabel = STATUS_LABELS[link.status] ?? link.status
            const entityType = link.entity_type ?? 'adhoc'
            const sourceColor = SOURCE_COLORS[entityType] ?? '#9ca3af'
            const sourceLabel = SOURCE_LABELS[entityType] ?? entityType

            return (
              <tr
                key={link.id}
                style={{
                  borderBottom: '1px solid #f3f4f6',
                  background: link.status === 'paid' ? '#f0fdf4' : undefined,
                }}
              >
                <td style={{ padding: '8px 12px', color: '#9ca3af', fontWeight: 500 }}>
                  #{link.id}
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: 10,
                      background: sourceColor + '18',
                      color: sourceColor,
                      fontWeight: 600,
                      fontSize: 11,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {sourceLabel}
                  </span>
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: 12,
                      background: statusColor + '22',
                      color: statusColor,
                      fontWeight: 600,
                      fontSize: 11,
                    }}
                  >
                    {statusLabel}
                  </span>
                </td>
                <td style={{ padding: '8px 12px', color: '#374151', whiteSpace: 'nowrap' }}>
                  {link.provider === 'yookassa' ? 'YooKassa' : 'Yandex Pay'}
                  {link.yandex_split && (
                    <span style={{ marginLeft: 4, color: '#8b5cf6', fontSize: 10 }}>(Split)</span>
                  )}
                </td>
                <td
                  style={{
                    padding: '8px 12px',
                    fontWeight: 700,
                    color: link.status === 'paid' ? '#15803d' : '#111827',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatAmount(link.amount_kopecks)}
                </td>
                <td
                  style={{
                    padding: '8px 12px',
                    color: '#6b7280',
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span title={[link.description, link.patient_label, link.patient_phone].filter(Boolean).join(' | ')}>
                    {link.description ?? ''}
                    {(link.patient_label ?? link.patient_phone) && (
                      <span style={{ color: '#9ca3af', marginLeft: 4 }}>
                        {link.patient_label ?? link.patient_phone}
                      </span>
                    )}
                    {!link.description && !link.patient_label && !link.patient_phone && '—'}
                  </span>
                </td>
                <td style={{ padding: '8px 12px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                  {formatDate(link.created_at)}
                </td>
                <td style={{ padding: '8px 12px', color: '#22c55e', whiteSpace: 'nowrap' }}>
                  {link.paid_at ? formatDate(link.paid_at) : '—'}
                </td>
                <td style={{ padding: '8px 12px' }}>
                  {link.payment_url && (
                    <button
                      type="button"
                      onClick={() => handleCopy(link)}
                      style={{
                        background: 'none',
                        border: '1px solid #e5e7eb',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 11,
                        padding: '3px 8px',
                        color: copiedId === link.id ? '#22c55e' : '#6b7280',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {copiedId === link.id ? '✓' : 'Ссылка'}
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onRefresh}
          style={{
            background: 'none',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            padding: '4px 12px',
            fontSize: 12,
            cursor: 'pointer',
            color: '#6b7280',
          }}
        >
          Обновить
        </button>
      </div>
    </div>
  )
}

// ─── Create link form ──────────────────────────────────────────────────────────

interface CreateFormState {
  provider: 'yookassa' | 'yandex_pay'
  amount_rub: string
  description: string
  yandex_split: boolean
  patient_phone: string
  patient_label: string
}

function CreateLinkForm({
  session,
  onCreated,
}: {
  session: AuthSession
  onCreated: (link: PaymentLink) => void
}) {
  const [form, setForm] = useState<CreateFormState>({
    provider: 'yookassa',
    amount_rub: '',
    description: '',
    yandex_split: false,
    patient_phone: '',
    patient_label: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<PaymentLink | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const amountKopecks = Math.round(parseFloat(form.amount_rub.replace(',', '.')) * 100)
    if (isNaN(amountKopecks) || amountKopecks <= 0) {
      setError('Введите корректную сумму.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const resp = await createAdhocPaymentLink(session, {
        provider: form.provider,
        amount_kopecks: amountKopecks,
        description: form.description || 'Оплата услуг',
        yandex_split: form.yandex_split,
        patient_phone: form.patient_phone || undefined,
        patient_label: form.patient_label || undefined,
      })
      const newLink: PaymentLink = {
        id: resp.data.id,
        entity_type: 'adhoc',
        provider: resp.data.provider,
        amount_kopecks: resp.data.amount_kopecks,
        amount_rub: resp.data.amount_kopecks / 100,
        currency: 'RUB',
        status: resp.data.status,
        yandex_split: form.yandex_split,
        payment_url: resp.data.payment_url,
        description: form.description,
        patient_phone: form.patient_phone || null,
        patient_label: form.patient_label || null,
      }
      setCreated(newLink)
      onCreated(newLink)
      setForm({
        provider: 'yookassa',
        amount_rub: '',
        description: '',
        yandex_split: false,
        patient_phone: '',
        patient_label: '',
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка создания ссылки')
    } finally {
      setSaving(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    padding: '7px 10px',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    fontSize: 13,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  }
  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 500, color: '#6b7280' }
  const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 8,
          padding: '10px 14px',
          fontSize: 12,
          color: '#1e40af',
        }}
      >
        💡 Разовые ссылки — без привязки к плану: депозит, доп. услуга, сертификат. Для шеринга
        плана используйте <strong>Планы → Поделиться</strong>.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={fieldStyle}>
          <label style={labelStyle}>Провайдер</label>
          <select
            style={inputStyle}
            value={form.provider}
            onChange={(e) =>
              setForm((f) => ({ ...f, provider: e.target.value as 'yookassa' | 'yandex_pay' }))
            }
          >
            <option value="yookassa">YooKassa</option>
            <option value="yandex_pay">Yandex Pay</option>
          </select>
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Сумма, ₽</label>
          <input
            style={inputStyle}
            type="number"
            min={1}
            step={0.01}
            placeholder="0.00"
            value={form.amount_rub}
            required
            onChange={(e) => setForm((f) => ({ ...f, amount_rub: e.target.value }))}
          />
        </div>
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Описание</label>
        <input
          style={inputStyle}
          type="text"
          maxLength={128}
          placeholder="Оплата услуг / депозит"
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={fieldStyle}>
          <label style={labelStyle}>Телефон пациента (необяз.)</label>
          <input
            style={inputStyle}
            type="tel"
            placeholder="+7..."
            value={form.patient_phone}
            onChange={(e) => setForm((f) => ({ ...f, patient_phone: e.target.value }))}
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Метка / ФИО (необяз.)</label>
          <input
            style={inputStyle}
            type="text"
            placeholder="Иванов Иван Иванович"
            value={form.patient_label}
            onChange={(e) => setForm((f) => ({ ...f, patient_label: e.target.value }))}
          />
        </div>
      </div>

      {form.provider === 'yandex_pay' && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.yandex_split}
            onChange={(e) => setForm((f) => ({ ...f, yandex_split: e.target.checked }))}
          />
          Включить Yandex Split (оплата в 4 части без процентов)
        </label>
      )}

      {error && <p style={{ fontSize: 12, color: '#ef4444', margin: 0 }}>{error}</p>}

      {created && (
        <div
          style={{
            background: '#f0fdf4',
            border: '1px solid #86efac',
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 12,
            color: '#15803d',
          }}
        >
          ✓ Ссылка создана.{' '}
          {created.payment_url && (
            <a
              href={created.payment_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#15803d', fontWeight: 600 }}
            >
              Открыть →
            </a>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={saving}
        style={{
          background: '#2563eb',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          padding: '9px 18px',
          fontSize: 13,
          fontWeight: 600,
          cursor: saving ? 'not-allowed' : 'pointer',
          opacity: saving ? 0.7 : 1,
          alignSelf: 'flex-start',
        }}
      >
        {saving ? 'Создаём...' : 'Создать ссылку'}
      </button>
    </form>
  )
}

// ─── Tabs ──────────────────────────────────────────────────────────────────────

type TabKey = 'all' | 'mis_plan' | 'constructor_plan' | 'adhoc' | 'create'

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'all', label: 'Все платежи' },
  { key: 'mis_plan', label: 'МИС планы' },
  { key: 'constructor_plan', label: 'Конструктор' },
  { key: 'adhoc', label: 'Разовые ссылки' },
  { key: 'create', label: '+ Создать ссылку' },
]

// ─── Main workspace ───────────────────────────────────────────────────────────

export function StaffPaymentLinksWorkspace({
  bootstrap,
  session,
  navigation,
  onNavigate,
  onLogout,
}: Props) {
  const [tab, setTab] = useState<TabKey>('all')
  const [stats, setStats] = useState<PaymentOverviewStats | null>(null)
  const [links, setLinks] = useState<PaymentLink[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')

  const sourceFilter = tab === 'all' || tab === 'create' ? undefined : tab

  const load = useCallback(async () => {
    if (tab === 'create') return
    setLoading(true)
    try {
      const resp = await getPaymentOverview(session, {
        source: sourceFilter,
        status: statusFilter || undefined,
        limit: 200,
      })
      setStats(resp.stats)
      setLinks(resp.data as PaymentLink[])
    } catch {
      // silently fail, user can retry
    } finally {
      setLoading(false)
    }
  }, [session, tab, sourceFilter, statusFilter])

  useEffect(() => {
    void load()
  }, [load])

  function handleLinkCreated(link: PaymentLink) {
    setLinks((prev) => [link, ...prev])
    if (stats) setStats((s) => (s ? { ...s, total: s.total + 1, pending: s.pending + 1 } : s))
    setTab('adhoc')
  }

  const tabBorderStyle = (key: TabKey): React.CSSProperties => ({
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: tab === key ? 700 : 500,
    color: tab === key ? '#2563eb' : '#6b7280',
    background: 'none',
    border: 'none',
    borderBottom: tab === key ? '2px solid #2563eb' : '2px solid transparent',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  })

  return (
    <StaffShell
      title="Платёжные ссылки"
      subtitle=""
      bootstrapTenant={bootstrap.tenant}
      navigation={navigation}
      currentView="payment-links"
      onNavigate={onNavigate}
      session={session}
      onLogout={onLogout}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '24px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {/* Header */}
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>
            Платёжные ссылки
          </h1>
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4, marginBottom: 0 }}>
            Единый реестр платежей по МИС-планам, Конструктору и разовым ссылкам
          </p>
        </div>

        {/* Stats */}
        <StatsRow stats={stats} />

        {/* Tab bar */}
        <div
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              borderBottom: '1px solid #e5e7eb',
              overflowX: 'auto',
              padding: '0 8px',
            }}
          >
            {TABS.map((t) => (
              <button key={t.key} type="button" style={tabBorderStyle(t.key)} onClick={() => setTab(t.key)}>
                {t.label}
              </button>
            ))}
          </div>

          <div style={{ padding: '20px 24px' }}>
            {tab === 'create' ? (
              <CreateLinkForm session={session} onCreated={handleLinkCreated} />
            ) : (
              <>
                {/* Filters row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
                    {tab === 'all' ? 'Все платежи' : TABS.find((t) => t.key === tab)?.label}
                  </span>
                  <div style={{ flex: 1 }} />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{
                      padding: '5px 10px',
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      fontSize: 12,
                      color: '#374151',
                    }}
                  >
                    <option value="">Все статусы</option>
                    <option value="paid">✓ Оплачено</option>
                    <option value="pending">Ожидает</option>
                    <option value="waiting_for_capture">Удержание</option>
                    <option value="canceled">Отменено</option>
                    <option value="expired">Истекло</option>
                  </select>
                </div>

                <UnifiedTable links={links} onRefresh={() => void load()} loading={loading} />
              </>
            )}
          </div>
        </div>
      </div>
    </StaffShell>
  )
}
